import React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface PagedToolbarProps {
  search: string;
  onSearch: (v: string) => void;
  placeholder?: string;
  authorFilter?: string;
  onClearAuthor?: () => void;
  testId?: string;
}

/** Small reusable toolbar for list pages: search input + optional author chip. */
export const PagedToolbar: React.FC<PagedToolbarProps> = ({
  search, onSearch, placeholder, authorFilter, onClearAuthor, testId = "list-search",
}) => (
  <div className="mb-4">
    <div className="relative max-w-xl">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--sg-muted)]" />
      <Input
        type="search"
        data-testid={testId}
        placeholder={placeholder || "Search…"}
        defaultValue={search}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSearch((e.target as HTMLInputElement).value.trim());
        }}
        className="pl-9 bg-white"
      />
    </div>
    {authorFilter && (
      <div
        className="mt-2 inline-flex items-center gap-2 text-xs bg-violet-50 text-violet-900 border border-violet-200 rounded-full px-2.5 py-1"
        data-testid={`${testId}-author-chip`}
      >
        <span>Filtering by user: <b>@{authorFilter}</b></span>
        <button
          type="button"
          data-testid={`${testId}-author-clear`}
          onClick={onClearAuthor}
          className="hover:text-violet-700"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    )}
  </div>
);


interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  testId?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  page, pageSize, total, onPageChange, onPageSizeChange, testId = "pagination",
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid={testId}>
      <div className="text-xs text-[color:var(--sg-muted)]">
        Showing <b>{(page - 1) * pageSize + 1}</b>–<b>{Math.min(page * pageSize, total)}</b> of <b>{total}</b>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs text-[color:var(--sg-muted)]">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            data-testid={`${testId}-size`}
            className="h-8 px-2 rounded-md border border-[color:var(--sg-border)] bg-white text-xs"
          >
            {[25, 50, 100, 200].map((n) => (<option key={n} value={n}>{n}</option>))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`${testId}-prev`}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="h-8 px-3 rounded-md border border-[color:var(--sg-border)] bg-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[color:var(--sg-muted-bg)]"
          >← Prev</button>
          <span className="text-xs px-3" data-testid={`${testId}-info`}>
            Page <b>{page}</b> of <b>{totalPages}</b>
          </span>
          <button
            type="button"
            data-testid={`${testId}-next`}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="h-8 px-3 rounded-md border border-[color:var(--sg-border)] bg-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[color:var(--sg-muted-bg)]"
          >Next →</button>
        </div>
      </div>
    </div>
  );
};
